<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import Button from "../Button.vue";
import { onboardingApi } from "../../api/onboarding";
import { useEscapeLayer } from "../../composables/escape";
import { t, type MessageKey } from "../../i18n";

/**
 * The attendant's short tour on first access: the queue, replying, transferring with a note and
 * closing with an outcome. Each step points at the real part of the inbox when it is on screen.
 */
const props = defineProps<{ slug: string }>();
const open = ref(false);
const index = ref(0);
const rect = ref<DOMRect | null>(null);
const STEPS = [
  ".chat-sidebar",
  ".composer-area",
  ".conversation-header",
  ".conversation-header .wa-button",
] as const;
const total = STEPS.length;

function measure() {
  const el = document.querySelector(STEPS[index.value]!);
  rect.value = el ? el.getBoundingClientRect() : null;
}
const card = computed(() => {
  const r = rect.value;
  const width = 340;
  if (!r)
    return {
      left: `calc(50% - ${width / 2}px)`,
      top: "30%",
      width: `${width}px`,
    };
  const right = r.right + 16 + width < innerWidth;
  const left = right ? r.right + 16 : Math.max(16, r.left - width - 16);
  const top = Math.min(Math.max(16, r.top + 24), innerHeight - 240);
  return { left: `${left}px`, top: `${top}px`, width: `${width}px` };
});
const spot = computed(() => {
  const r = rect.value;
  return r
    ? {
        left: `${r.left - 4}px`,
        top: `${r.top - 4}px`,
        width: `${r.width + 8}px`,
        height: `${r.height + 8}px`,
      }
    : null;
});

async function finish(outcome: "completed" | "skipped") {
  open.value = false;
  await onboardingApi.tourDone(props.slug, outcome).catch(() => undefined);
}
async function next() {
  if (index.value === total - 1) return finish("completed");
  index.value += 1;
  await nextTick();
  measure();
}
useEscapeLayer(
  () => open.value,
  () => void finish("skipped"),
);

onMounted(async () => {
  const r = await onboardingApi.tour(props.slug).catch(() => null);
  if (!r?.attendant.show) return;
  open.value = true;
  await nextTick();
  measure();
  window.addEventListener("resize", measure);
});
onBeforeUnmount(() => window.removeEventListener("resize", measure));
</script>
<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="tour-layer"
      role="dialog"
      aria-modal="true"
      :aria-label="t(`tour.${index + 1}.title` as MessageKey)"
    >
      <div v-if="spot" class="tour-spot" :style="spot" />
      <div v-else class="tour-dim" />
      <div class="tour-card" :style="card">
        <span class="journey-eyebrow">{{
          t("tour.step", { n: index + 1, total })
        }}</span>
        <h3>{{ t(`tour.${index + 1}.title` as MessageKey) }}</h3>
        <p>{{ t(`tour.${index + 1}.text` as MessageKey) }}</p>
        <div class="tour-actions">
          <Button variant="ghost" compact @click="finish('skipped')">{{
            t("tour.skip")
          }}</Button>
          <Button compact @click="next">{{
            index === total - 1 ? t("tour.done") : t("tour.next")
          }}</Button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
